import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/App";
import { candidateAPI, paymentsAPI, authAPI, jobsAPI } from "@/lib/api";
import { useTranslation, EUROPEAN_COUNTRIES, POSITIONS_LIST, COUNTRY_FLAGS, PRIORITY_COUNTRIES } from "@/lib/i18n";
import { SettingsView } from "@/components/shared/SettingsView";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Logo } from "@/components/shared/Logo";
import {
  User, LogOut, Save, Crown, Plus, X, Languages, FileText, Upload,
  Trash2, Shield, Sparkles, ArrowRight, AlertTriangle, ChevronLeft,
  Pencil, CheckCircle2, Circle, Star, Camera, MessageSquare, Video, Play, Square, RotateCcw, Settings, Briefcase, MapPin, Building2, Clock
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessagesView, ProfileViewsCard } from "@/components/MessagesView";

const LANGUAGE_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2", "native"];
const COMMON_LANGUAGES = [
  "Spanish", "English", "French", "German", "Italian", "Portuguese",
  "Dutch", "Russian", "Arabic", "Chinese", "Japanese", "Polish",
  "Romanian", "Greek", "Turkish", "Swedish", "Norwegian", "Danish",
  "Finnish", "Czech", "Hungarian", "Croatian", "Bulgarian", "Ukrainian",
];

function getProfileCompleteness(profile) {
  const checks = [
    !!profile?.nationality,
    !!profile?.country,
    !!profile?.phone,
    !!profile?.bio,
    (profile?.desired_positions?.length || 0) > 0,
    (profile?.languages?.length || 0) > 0,
    !!profile?.cv_url,
  ];
  const filled = checks.filter(Boolean).length;
  return Math.round((filled / checks.length) * 100);
}

/* ── Overview Hub ─────────────────────────────── */
function OverviewView({ profile, user, t, tc, tp, onEdit, onPremium, onToggle, onLogout, onDeleteOpen, onPhotoClick, onMessages, onSettings, onJobs, unreadCount, setProfile }) {
  const pct = useMemo(() => getProfileCompleteness(profile), [profile]);
  const isComplete = pct === 100;
  const isPremium = profile?.is_premium;
  const photoUrl = profile?.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}?t=${Date.now()}` : null;

  return (
    <div className="max-w-lg mx-auto px-4 md:px-6 py-6 space-y-5">
      {/* Welcome + Avatar */}
      <div className="flex items-center gap-4" data-testid="welcome-section">
        <button
          onClick={onPhotoClick}
          className="relative w-16 h-16 rounded-full flex-shrink-0 overflow-hidden group focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2"
          data-testid="profile-photo-btn"
        >
          {photoUrl ? (
            <img src={photoUrl} alt="" className="w-full h-full object-cover" data-testid="profile-photo-img" />
          ) : (
            <div className="w-full h-full bg-navy-900 flex items-center justify-center">
              <span className="text-white font-sans font-bold text-xl">{(user?.name || "?")[0]?.toUpperCase()}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Camera className="w-5 h-5 text-white" />
          </div>
        </button>
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" data-testid="welcome-heading">
            {t("cd.welcome", { name: user?.name?.split(" ")[0] || "" })}
          </h1>
          <p className="text-sm text-slate-500 font-body mt-1" data-testid="member-since">
            {t("cd.member_since", { date: user?.created_at?.split("T")[0] || "" })}
          </p>
        </div>
      </div>

      {/* Profile completeness */}
      <Card className="border-slate-200 bg-white shadow-sm" data-testid="profile-completeness-card">
        <CardContent className="py-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isComplete
                ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                : <Circle className="w-5 h-5 text-amber-500" />}
              <span className={`text-sm font-semibold font-body ${isComplete ? "text-emerald-700" : "text-amber-700"}`} data-testid="profile-status-label">
                {isComplete ? t("cd.profile_complete") : t("cd.profile_incomplete")}
              </span>
            </div>
            <span className="text-xs font-body text-slate-500" data-testid="profile-pct">{t("cd.complete_pct", { pct })}</span>
          </div>
          {/* Progress bar */}
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${isComplete ? "bg-emerald-500" : "bg-amber-500"}`}
              style={{ width: `${pct}%` }}
              data-testid="profile-progress-bar"
            />
          </div>
          <p className="text-xs text-slate-500 font-body">
            {isComplete ? t("cd.complete_hint_done") : t("cd.complete_hint")}
          </p>
          {!isComplete && (
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-1 font-body border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={onEdit}
              data-testid="complete-profile-btn"
            >
              <Pencil className="w-3.5 h-3.5 mr-2" /> {t("cd.edit_profile")}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Plan block */}
      {isPremium ? (
        <Card className="border-gold-500/30 bg-gradient-to-br from-navy-900 to-navy-800 shadow-lg" data-testid="plan-card-premium">
          <CardContent className="py-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gold-500/20 flex items-center justify-center">
                <Crown className="w-5 h-5 text-gold-400" />
              </div>
              <div>
                <p className="font-sans font-bold text-white text-base" data-testid="plan-name">{t("cd.plan_premium_label")}</p>
                <p className="text-xs text-gold-400 font-body">{t("cd.plan_premium_desc")}</p>
              </div>
            </div>
            <div className="space-y-2 mt-4">
              {["cd.upsell_f1", "cd.upsell_f2", "cd.upsell_f3"].map((k) => (
                <div key={k} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-gold-400 flex-shrink-0" />
                  <span className="text-sm text-slate-200 font-body">{t(k)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 bg-white shadow-sm" data-testid="plan-card-free">
          <CardContent className="py-5">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <User className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="font-sans font-semibold text-slate-900 text-base" data-testid="plan-name">{t("cd.plan_free")}</p>
                <p className="text-xs text-slate-500 font-body">{t("cd.plan_free_desc")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upsell */}
      {!isPremium && (
        <Card className="border-0 bg-navy-900 shadow-xl overflow-hidden" data-testid="upsell-card">
          <CardContent className="py-6 px-5">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-gold-400" />
              <h3 className="font-sans font-bold text-white text-base">{t("cd.upsell_title")}</h3>
            </div>
            <p className="text-sm text-slate-300 font-body mb-4 leading-relaxed">{t("cd.upsell_desc")}</p>
            <div className="space-y-2 mb-5">
              {["cd.upsell_f1", "cd.upsell_f2", "cd.upsell_f3"].map((k) => (
                <div key={k} className="flex items-center gap-2">
                  <Star className="w-3.5 h-3.5 text-gold-500 flex-shrink-0" />
                  <span className="text-sm text-slate-200 font-body">{t(k)}</span>
                </div>
              ))}
            </div>
            <Button
              className="w-full bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-lg h-12 text-sm"
              onClick={onPremium}
              data-testid="upgrade-premium-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              {t("cd.upsell_btn")}
              <span className="ml-auto text-xs opacity-80 font-body">{t("cd.upsell_price")}</span>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Profile Views */}
      <ProfileViewsCard t={t} isPremium={isPremium} onPremium={onPremium} />

      {/* Preferred Countries Display */}
      {(profile?.preferred_countries || []).length > 0 && (
        <Card className="border-slate-200 bg-white shadow-sm" data-testid="overview-preferred-countries">
          <CardContent className="py-4">
            <p className="text-xs font-semibold text-slate-500 font-body uppercase tracking-wider mb-2">{t("cd.preferred_countries")}</p>
            <div className="flex flex-wrap gap-2">
              {profile.preferred_countries.map(c => (
                <span key={c} className="inline-flex items-center gap-1.5 bg-sky-50 text-sky-800 border border-sky-200 rounded-full px-3 py-1 text-sm font-body">
                  <span className="text-base">{COUNTRY_FLAGS[c] || ""}</span> {tc(c)}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Video Presentation Display */}
      {profile?.video_url && (
        <Card className="border-slate-200 bg-white shadow-sm" data-testid="overview-video">
          <CardContent className="py-4">
            <p className="text-xs font-semibold text-slate-500 font-body uppercase tracking-wider mb-2">{t("cd.video_title")}</p>
            <div className="rounded-lg overflow-hidden border border-slate-200 bg-black">
              <video
                src={`${candidateAPI.getVideoUrl(user.user_id)}?t=${Date.now()}`}
                controls
                className="w-full max-h-[220px]"
                data-testid="overview-video-player"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick settings */}
      <div className="space-y-3" data-testid="quick-settings">
        <h3 className="text-sm font-semibold text-slate-700 font-body">{t("cd.quick_settings")}</h3>
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="py-0 divide-y divide-slate-100">
            <div className="flex items-center justify-between py-4">
              <div>
                <p className="text-sm font-semibold text-slate-900 font-body">{t("cd.available_title")}</p>
                <p className="text-xs text-slate-500 font-body">{t("cd.available_desc")}</p>
              </div>
              <div className="flex items-center gap-2">
                {profile?.available_now && <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
                <Switch
                  checked={profile?.available_now || false}
                  onCheckedChange={(v) => {
                    onToggle("available_now", v);
                    if (v) setProfile(p => ({ ...p, available_from: null }));
                  }}
                  data-testid="available-now-switch"
                />
              </div>
            </div>
            {!profile?.available_now && (
              <div className="flex items-center justify-between py-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900 font-body">{t("cd.available_from")}</p>
                  <p className="text-xs text-slate-500 font-body">{t("cd.available_from_desc")}</p>
                </div>
                <Input
                  type="date"
                  value={profile?.available_from || ""}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setProfile(p => ({ ...p, available_from: e.target.value }))}
                  className="w-40 h-9 text-sm font-body"
                  data-testid="available-from-date"
                />
              </div>
            )}
            <div className="flex items-center justify-between py-4">
              <div>
                <p className="text-sm font-semibold text-slate-900 font-body">{t("cd.needs_accommodation")}</p>
                <p className="text-xs text-slate-500 font-body">{t("cd.needs_accommodation_desc")}</p>
              </div>
              <div className="flex items-center gap-2">
                {profile?.needs_accommodation && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                <Switch
                  checked={profile?.needs_accommodation || false}
                  onCheckedChange={(v) => onToggle("needs_accommodation", v)}
                  data-testid="needs-accommodation-switch"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 flex-wrap" data-testid="action-buttons">
        <Button
          className="flex-1 bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-lg h-12"
          onClick={onEdit}
          data-testid="edit-profile-btn"
        >
          <Pencil className="w-4 h-4 mr-2" /> {t("cd.edit_profile")}
        </Button>
        <Button
          className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg h-12"
          onClick={onJobs}
          data-testid="browse-jobs-btn"
        >
          <Briefcase className="w-4 h-4 mr-2" /> {t("cd.browse_jobs")}
        </Button>
        <Button
          variant="outline"
          className="h-12 px-5 border-slate-300 text-slate-600 hover:text-gold-600 hover:border-gold-300 rounded-lg relative"
          onClick={onMessages}
          data-testid="messages-btn"
        >
          <MessageSquare className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-gold-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="unread-badge">
              {unreadCount}
            </span>
          )}
        </Button>
        <Button
          variant="outline"
          className="h-12 px-5 border-slate-300 text-slate-600 hover:text-slate-800 hover:border-slate-400 rounded-lg"
          onClick={onSettings}
          data-testid="settings-btn"
        >
          <Settings className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          className="h-12 px-5 border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-300 rounded-lg"
          onClick={onLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      {/* Footer links */}
      <div className="text-center pt-2 space-x-3">
        <Link to="/privacy" target="_blank" className="text-xs text-slate-400 hover:text-gold-600 font-body transition-colors" data-testid="candidate-privacy-link">
          <Shield className="w-3 h-3 inline mr-1" />{t("auth.privacy_link")}
        </Link>
        <span className="text-slate-300">|</span>
        <Link to="/terms" target="_blank" className="text-xs text-slate-400 hover:text-gold-600 font-body transition-colors" data-testid="candidate-terms-link">
          {t("tos.link")}
        </Link>
        <span className="text-slate-300">|</span>
        <button onClick={onDeleteOpen} className="text-xs text-red-400 hover:text-red-600 font-body transition-colors" data-testid="open-delete-account-btn">
          <Trash2 className="w-3 h-3 inline mr-1" />{t("account.delete_title")}
        </button>
      </div>
    </div>
  );
}

/* ── Candidate Jobs View ──────────────────────────── */
const EMPLOYMENT_LABELS = {
  full_time: "Full-time", part_time: "Part-time", temporary: "Temporary",
  seasonal: "Seasonal", internship: "Internship",
};

function CandidateJobsView({ profile, t, tc, tp, onBack }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ country: "", position: "" });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Pre-fill filters from candidate profile
    const initCountry = profile?.preferred_countries?.[0] || profile?.country || "";
    const initPosition = profile?.desired_positions?.[0] || "";
    setFilters({ country: initCountry, position: initPosition });
  }, []);

  useEffect(() => { loadJobs(); }, [filters.country, filters.position, page]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.country) params.country = filters.country;
      if (filters.position) params.position = filters.position;
      const res = await jobsAPI.getPublic(params);
      setJobs(res.data.jobs || []);
      setTotal(res.data.total || 0);
    } catch {} finally { setLoading(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 md:px-6 py-6" data-testid="candidate-jobs-view">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-gold-600 font-body mb-4 transition-colors" data-testid="back-from-jobs">
        <ChevronLeft className="w-4 h-4" /> {t("cd.back_overview")}
      </button>

      <h2 className="font-sans font-bold text-xl text-slate-900 mb-4 flex items-center gap-2">
        <Briefcase className="w-5 h-5 text-emerald-500" /> {t("cd.job_offers")}
      </h2>

      {/* Filters */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <Select value={filters.country || "all"} onValueChange={v => { setFilters(f => ({ ...f, country: v === "all" ? "" : v })); setPage(1); }}>
          <SelectTrigger className="h-10 font-body text-sm" data-testid="cand-jobs-country">
            <SelectValue placeholder={t("hd.all_countries")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("hd.all_countries")}</SelectItem>
            {EUROPEAN_COUNTRIES.map(c => <SelectItem key={c} value={c}>{COUNTRY_FLAGS[c]} {tc(c)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.position || "all"} onValueChange={v => { setFilters(f => ({ ...f, position: v === "all" ? "" : v })); setPage(1); }}>
          <SelectTrigger className="h-10 font-body text-sm" data-testid="cand-jobs-position">
            <SelectValue placeholder={t("hd.all_positions")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("hd.all_positions")}</SelectItem>
            {POSITIONS_LIST.map(p => <SelectItem key={p} value={p}>{tp(p)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <p className="text-xs text-slate-400 font-body mb-3">{total} {t("cd.jobs_found")}</p>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : jobs.length === 0 ? (
        <Card className="border-slate-200 bg-white">
          <CardContent className="py-10 text-center">
            <Briefcase className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 font-body">{t("cd.no_jobs")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {jobs.map(job => {
            const loc = [job.city, job.region, tc(job.country)].filter(Boolean).join(", ");
            return (
              <Card key={job.job_id} className="border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer"
                onClick={() => navigate(`/jobs/${job.slug}`)} data-testid={`cand-job-${job.job_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-navy-900 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-sans font-semibold text-slate-900 text-sm truncate">{job.title}</h3>
                      <p className="text-xs text-slate-500 font-body mb-1.5">{job.hotel_name}</p>
                      <div className="flex flex-wrap gap-1.5">
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-body">{tp(job.position)}</Badge>
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px] font-body"><MapPin className="w-2.5 h-2.5 mr-0.5" />{loc}</Badge>
                        <Badge className="bg-sky-50 text-sky-700 border-sky-200 text-[10px] font-body">{EMPLOYMENT_LABELS[job.employment_type]}</Badge>
                        {(job.salary_min || job.salary_max) && (
                          <Badge className="bg-gold-50 text-gold-700 border-gold-200 text-[10px] font-body">
                            {job.salary_min && job.salary_max ? `${job.salary_min}-${job.salary_max}` : job.salary_min || job.salary_max} EUR
                          </Badge>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-2" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {total > 20 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={jobs.length < 20} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}

/* ── Edit Profile View ──────────────────────────── */
function EditProfileView({
  profile, user, t, tc, tp, onBack, updateField, saveProfile, saving,
  uploadingCV, handleCVUpload, handleCVDelete, togglePosition,
  newLang, setNewLang, addLanguage, removeLanguage, langInputRef,
  uploadingPhoto, handlePhotoUpload, handlePhotoDelete, onPhotoUpsell,
  uploadingVideo, handleVideoUpload, handleVideoDelete,
  recording, recordedBlob, recordingTime, videoPreviewRef, liveVideoRef,
  startRecording, stopRecording, saveRecordedVideo, discardRecording,
  onVideoUpsell,
}) {
  const isPremium = profile?.is_premium;
  const photoUrl = profile?.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}?t=${Date.now()}` : null;

  const onPhotoInputChange = (e) => {
    if (!isPremium) { onPhotoUpsell(); e.target.value = ""; return; }
    handlePhotoUpload(e);
  };

  const onPhotoLabelClick = (e) => {
    if (!isPremium) {
      e.preventDefault();
      onPhotoUpsell();
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-6">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-gold-600 font-body mb-4 transition-colors"
        data-testid="back-to-overview-btn"
      >
        <ChevronLeft className="w-4 h-4" /> {t("cd.back_overview")}
      </button>

      <h2 className="font-sans text-xl font-bold text-slate-900 mb-5 flex items-center gap-2">
        <User className="w-5 h-5 text-gold-500" /> {t("cd.profile_title")}
      </h2>

      <div className="space-y-6">
        {/* Profile Photo */}
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="pt-5 pb-5">
            <Label className="font-body text-sm text-slate-600 mb-3 block">{t("cd.photo_title")}</Label>
            <div className="flex items-center gap-5">
              <div className="relative w-20 h-20 rounded-full overflow-hidden flex-shrink-0 bg-slate-100">
                {photoUrl ? (
                  <img src={photoUrl} alt="" className="w-full h-full object-cover" data-testid="edit-photo-preview" />
                ) : (
                  <div className="w-full h-full bg-navy-900 flex items-center justify-center">
                    <User className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                {uploadingPhoto && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body cursor-pointer transition-colors ${
                    isPremium
                      ? "bg-gold-50 text-gold-700 border border-gold-200 hover:bg-gold-100"
                      : "bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100"
                  }`}
                  data-testid="photo-upload-label"
                  onClick={onPhotoLabelClick}
                >
                  <Camera className="w-4 h-4" />
                  {photoUrl ? t("cd.photo_change") : t("cd.photo_upload")}
                  {!isPremium && <Crown className="w-3.5 h-3.5 text-gold-500" />}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="hidden"
                    onChange={onPhotoInputChange}
                    disabled={uploadingPhoto}
                    data-testid="photo-file-input"
                  />
                </label>
                {photoUrl && isPremium && (
                  <button
                    onClick={handlePhotoDelete}
                    className="text-xs text-red-400 hover:text-red-600 font-body transition-colors text-left"
                    data-testid="photo-delete-btn"
                  >
                    <Trash2 className="w-3 h-3 inline mr-1" />{t("cd.photo_delete")}
                  </button>
                )}
                <p className="text-xs text-slate-400 font-body">JPG/PNG, max 5MB</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personal info */}
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="pt-5 pb-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-body text-sm text-slate-600">{t("cd.nationality")}</Label>
                <Input value={profile?.nationality || ""} onChange={(e) => updateField("nationality", e.target.value)} className="mt-1 h-11 font-body" data-testid="input-nationality" />
              </div>
              <div>
                <Label className="font-body text-sm text-slate-600">{t("cd.age")}</Label>
                <Input type="number" value={profile?.age || ""} onChange={(e) => updateField("age", e.target.value)} className="mt-1 h-11 font-body" data-testid="input-age" />
              </div>
              <div>
                <Label className="font-body text-sm text-slate-600">{t("cd.location")}</Label>
                <Input value={profile?.location || ""} onChange={(e) => updateField("location", e.target.value)} className="mt-1 h-11 font-body" data-testid="input-location" />
              </div>
              <div>
                <Label className="font-body text-sm text-slate-600">{t("cd.country")}</Label>
                <Select value={profile?.country || ""} onValueChange={(v) => updateField("country", v)}>
                  <SelectTrigger className="mt-1 h-11 font-body" data-testid="input-country"><SelectValue placeholder={t("cd.country")} /></SelectTrigger>
                  <SelectContent>
                    {EUROPEAN_COUNTRIES.map(c => <SelectItem key={c} value={c}>{COUNTRY_FLAGS[c]} {tc(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-body text-sm text-slate-600">{t("cd.phone")}</Label>
                <Input value={profile?.phone || ""} onChange={(e) => updateField("phone", e.target.value)} placeholder="+34 612 345 678" className="mt-1 h-11 font-body" data-testid="input-phone" />
              </div>
              <div>
                <Label className="font-body text-sm text-slate-600">{t("cd.experience")}</Label>
                <Input type="number" value={profile?.experience_years || ""} onChange={(e) => updateField("experience_years", e.target.value)} className="mt-1 h-11 font-body" data-testid="input-experience" />
              </div>
              <div>
                <Label className="font-body text-sm text-slate-600">{t("cd.salary")}</Label>
                <Input type="number" value={profile?.expected_salary || ""} onChange={(e) => updateField("expected_salary", e.target.value)} className="mt-1 h-11 font-body" data-testid="input-salary" />
              </div>
            </div>

            <div>
              <Label className="font-body text-sm text-slate-600">{t("cd.bio")}</Label>
              <Textarea value={profile?.bio || ""} onChange={(e) => updateField("bio", e.target.value)} placeholder={t("cd.bio_placeholder")} className="mt-1 font-body min-h-[80px]" data-testid="input-bio" />
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={profile?.willing_to_relocate || false} onCheckedChange={(v) => updateField("willing_to_relocate", v)} data-testid="relocate-switch" />
              <Label className="font-body text-sm text-slate-600">{t("cd.relocate")}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Preferred Countries */}
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="pt-5 pb-5">
            <Label className="font-body text-sm text-slate-600 mb-1 block">{t("cd.preferred_countries")}</Label>
            <p className="text-xs text-slate-400 font-body mb-3">{t("cd.preferred_countries_desc")}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {(profile?.preferred_countries || []).map(c => (
                <Badge key={c} className="bg-sky-50 text-sky-800 border-sky-200 font-body text-sm gap-1.5 pr-1.5" data-testid={`pref-country-${c}`}>
                  <span>{COUNTRY_FLAGS[c] || ""}</span> {tc(c)}
                  <button onClick={() => updateField("preferred_countries", (profile.preferred_countries || []).filter(x => x !== c))}
                    className="ml-1 rounded-full hover:bg-sky-200 p-0.5 transition-colors" data-testid={`remove-pref-${c}`}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {(profile?.preferred_countries || []).length === 0 && (
                <p className="text-xs text-slate-400 font-body italic">{t("cd.preferred_countries_empty")}</p>
              )}
            </div>
            {(profile?.preferred_countries || []).length < 3 && (
              <Select onValueChange={(v) => {
                const current = profile?.preferred_countries || [];
                if (!current.includes(v)) updateField("preferred_countries", [...current, v]);
              }}>
                <SelectTrigger className="h-11 font-body w-full sm:w-64" data-testid="pref-country-select">
                  <SelectValue placeholder={t("cd.preferred_countries_select")} />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_COUNTRIES.filter(c => !(profile?.preferred_countries || []).includes(c)).map(c => (
                    <SelectItem key={c} value={c}><span className="mr-2">{COUNTRY_FLAGS[c]}</span>{tc(c)}</SelectItem>
                  ))}
                  <Separator className="my-1" />
                  {EUROPEAN_COUNTRIES.filter(c => !PRIORITY_COUNTRIES.includes(c) && !(profile?.preferred_countries || []).includes(c)).map(c => (
                    <SelectItem key={c} value={c}><span className="mr-2">{COUNTRY_FLAGS[c]}</span>{tc(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(profile?.preferred_countries || []).length >= 3 && (
              <p className="text-xs text-amber-600 font-body">{t("cd.preferred_countries_max")}</p>
            )}
          </CardContent>
        </Card>

        {/* CV */}
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="pt-5 pb-5">
            <Label className="font-body text-sm text-slate-600 mb-3 block">{t("cd.cv_title")}</Label>
            {profile?.cv_url ? (
              <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-4 border border-slate-200">
                <FileText className="w-8 h-8 text-gold-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 font-body truncate">{profile.cv_original_name || "CV.pdf"}</p>
                  <p className="text-xs text-slate-500 font-body">{t("cd.cv_uploaded_at")} {profile.cv_uploaded_at?.split("T")[0] || ""}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="font-body" onClick={() => window.open(candidateAPI.getCVUrl(user.user_id), '_blank')} data-testid="view-cv-btn">
                    <FileText className="w-3 h-3 mr-1" /> {t("cd.cv_view")}
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={handleCVDelete} data-testid="delete-cv-btn">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-6 cursor-pointer hover:border-gold-500 hover:bg-gold-50/30 transition-colors" data-testid="cv-upload-area">
                <Upload className="w-8 h-8 text-slate-400" />
                <p className="text-sm text-slate-600 font-body">{t("cd.cv_upload")}</p>
                <p className="text-xs text-slate-400 font-body">{t("cd.cv_format")}</p>
                <input type="file" accept=".pdf" className="hidden" onChange={handleCVUpload} disabled={uploadingCV} data-testid="cv-file-input" />
                {uploadingCV && <div className="w-5 h-5 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />}
              </label>
            )}
          </CardContent>
        </Card>

        {/* Video Presentation (Premium) */}
        <Card className="border-slate-200 bg-white shadow-sm" data-testid="video-section">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center gap-2 mb-1">
              <Label className="font-body text-sm text-slate-600">{t("cd.video_title")}</Label>
              <Crown className="w-3.5 h-3.5 text-gold-500" />
            </div>
            <p className="text-xs text-slate-400 font-body mb-3">{t("cd.video_desc")}</p>

            {profile?.video_url && isPremium ? (
              <div className="space-y-3">
                <div className="rounded-lg overflow-hidden border border-slate-200 bg-black">
                  <video
                    src={`${candidateAPI.getVideoUrl(user.user_id)}?t=${Date.now()}`}
                    controls
                    className="w-full max-h-[280px]"
                    data-testid="video-player"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-slate-500 font-body flex-1">
                    {profile.video_uploaded_at?.split("T")[0] || ""}
                  </p>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={handleVideoDelete} data-testid="delete-video-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> {t("cd.video_delete")}
                  </Button>
                </div>
              </div>
            ) : recordedBlob && isPremium ? (
              <div className="space-y-3">
                <div className="rounded-lg overflow-hidden border border-slate-200 bg-black">
                  <video
                    ref={videoPreviewRef}
                    src={URL.createObjectURL(recordedBlob)}
                    controls
                    className="w-full max-h-[280px]"
                    data-testid="video-preview"
                  />
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1 bg-gold-500 hover:bg-gold-600 text-white font-semibold" onClick={saveRecordedVideo} disabled={uploadingVideo} data-testid="save-video-btn">
                    {uploadingVideo ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    {t("cd.video_save")}
                  </Button>
                  <Button variant="outline" onClick={discardRecording} data-testid="discard-video-btn">
                    <RotateCcw className="w-4 h-4 mr-1" /> {t("cd.video_retry")}
                  </Button>
                </div>
              </div>
            ) : recording && isPremium ? (
              <div className="space-y-3">
                <div className="rounded-lg overflow-hidden border-2 border-red-400 bg-black relative">
                  <video ref={liveVideoRef} muted className="w-full max-h-[280px] mirror" data-testid="video-live" />
                  <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1.5 animate-pulse">
                    <div className="w-2 h-2 rounded-full bg-white" />
                    {t("cd.video_time_left", { sec: recordingTime })}
                  </div>
                </div>
                <Button className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold" onClick={stopRecording} data-testid="stop-recording-btn">
                  <Square className="w-4 h-4 mr-2" /> {t("cd.video_stop")}
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 h-20 flex-col gap-1.5 border-slate-200 hover:border-sky-300 hover:bg-sky-50"
                  onClick={isPremium ? startRecording : () => onVideoUpsell()} data-testid="record-video-btn">
                  <Video className="w-6 h-6 text-sky-600" />
                  <span className="text-xs font-body">{t("cd.video_record")}</span>
                </Button>
                {isPremium ? (
                  <label className="flex-1 h-20 flex flex-col items-center justify-center gap-1.5 border border-dashed border-slate-200 rounded-md cursor-pointer hover:border-gold-400 hover:bg-gold-50/30 transition-colors" data-testid="upload-video-area">
                    <Upload className="w-6 h-6 text-gold-500" />
                    <span className="text-xs font-body text-slate-600">{t("cd.video_upload")}</span>
                    <span className="text-[10px] text-slate-400 font-body">{t("cd.video_format")}</span>
                    <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={handleVideoUpload} disabled={uploadingVideo} data-testid="video-file-input" />
                    {uploadingVideo && <div className="w-5 h-5 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />}
                  </label>
                ) : (
                  <button type="button" className="flex-1 h-20 flex flex-col items-center justify-center gap-1.5 border border-dashed border-slate-200 rounded-md hover:border-gold-400 hover:bg-gold-50/30 transition-colors"
                    onClick={() => onVideoUpsell()} data-testid="upload-video-area">
                    <Upload className="w-6 h-6 text-gold-500" />
                    <span className="text-xs font-body text-slate-600">{t("cd.video_upload")}</span>
                    <span className="text-[10px] text-slate-400 font-body">{t("cd.video_format")}</span>
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Positions */}
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="pt-5 pb-5">
            <Label className="font-body text-sm text-slate-600 mb-3 block">{t("cd.positions")}</Label>
            <div className="flex flex-wrap gap-2">
              {POSITIONS_LIST.map(pos => (
                <Badge key={pos} variant={profile?.desired_positions?.includes(pos) ? "default" : "outline"}
                  className={`cursor-pointer transition-colors font-body ${
                    profile?.desired_positions?.includes(pos) ? "bg-gold-500 text-white hover:bg-gold-600 border-gold-500" : "border-slate-300 text-slate-600 hover:border-gold-500"
                  }`} onClick={() => togglePosition(pos)} data-testid={`position-${pos}`}>
                  {tp(pos)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Languages */}
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="pt-5 pb-5">
            <Label className="font-body text-sm text-slate-600 mb-3 block">{t("cd.languages")}</Label>
            <div className="space-y-2 mb-3">
              {(profile?.languages || []).map((l, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                  <Languages className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-body text-slate-700 flex-1">{l.language}</span>
                  <Badge variant="outline" className="font-body text-xs">{l.level}</Badge>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeLanguage(i)} data-testid={`remove-lang-${i}`}><X className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
            {/* Quick-add common language chips */}
            {(() => {
              const existingLangs = (profile?.languages || []).map(l => l.language.toLowerCase());
              const suggestions = COMMON_LANGUAGES.filter(l => !existingLangs.includes(l.toLowerCase())).slice(0, 6);
              if (suggestions.length === 0) return null;
              return (
                <div className="mb-3" data-testid="lang-suggestions">
                  <p className="text-xs text-slate-400 font-body mb-1.5">{t("cd.add_language")}:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map(lang => (
                      <button
                        key={lang}
                        type="button"
                        className="text-xs px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-sky-400 hover:text-sky-600 hover:bg-sky-50 transition-colors font-body"
                        onClick={() => setNewLang(p => ({ ...p, language: lang }))}
                        data-testid={`lang-chip-${lang.toLowerCase()}`}
                      >
                        + {lang}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div className="flex gap-2">
              <Input
                ref={langInputRef}
                value={newLang.language}
                onChange={(e) => setNewLang(p => ({ ...p, language: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLanguage(); } }}
                placeholder={t("cd.add_language")}
                className="h-10 font-body flex-1"
                data-testid="new-lang-input"
              />
              <Select value={newLang.level} onValueChange={(v) => setNewLang(p => ({ ...p, level: v }))}>
                <SelectTrigger className="w-24 h-10 font-body" data-testid="new-lang-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGUAGE_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-10 px-3" onClick={addLanguage} data-testid="add-lang-btn"><Plus className="w-4 h-4" /></Button>
            </div>
          </CardContent>
        </Card>

        {/* Save */}
        <Button className="bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-lg w-full h-12" onClick={saveProfile} disabled={saving} data-testid="save-profile-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? t("cd.saving") : t("cd.save")}
        </Button>
      </div>
    </div>
  );
}

/* ── Main Component ─────────────────────────────── */
export default function CandidateDashboard() {
  const { user, logout } = useAuth();
  const { t, tp, tc } = useTranslation();
  const [view, setView] = useState("overview");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCV, setUploadingCV] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoUpsell, setShowPhotoUpsell] = useState(false);
  const [showVideoUpsell, setShowVideoUpsell] = useState(false);
  const [newLang, setNewLang] = useState({ language: "", level: "B1" });
  const langInputRef = useRef(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(30);
  const mediaRecorderRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const liveVideoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem("staffera_welcome") === "1") {
      sessionStorage.removeItem("staffera_welcome");
      setShowWelcome(true);
    }
  }, []);

  useEffect(() => { loadProfile(); loadUnread(); }, []);

  const loadUnread = async () => {
    try {
      const { messagesAPI } = await import("@/lib/api");
      const res = await messagesAPI.getUnreadCount();
      setUnreadCount(res.data.unread || 0);
    } catch {}
  };

  const loadProfile = async () => {
    try {
      const res = await candidateAPI.getProfile();
      setProfile(res.data);
    } catch { toast.error("Error"); } finally { setLoading(false); }
  };

  const updateField = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));

  const handleToggle = async (field, value) => {
    updateField(field, value);
    try {
      await candidateAPI.updateProfile({ [field]: value });
    } catch { toast.error("Error"); }
  };

  const togglePosition = (pos) => {
    const current = profile.desired_positions || [];
    updateField("desired_positions", current.includes(pos) ? current.filter(p => p !== pos) : [...current, pos]);
  };

  const addLanguage = () => {
    if (!newLang.language.trim()) {
      langInputRef.current?.focus();
      toast.info(t("cd.type_language_first"));
      return;
    }
    const duplicate = (profile.languages || []).some(
      l => l.language.toLowerCase() === newLang.language.trim().toLowerCase()
    );
    if (duplicate) {
      toast.warning(t("cd.language_already_added"));
      setNewLang({ language: "", level: "B1" });
      return;
    }
    updateField("languages", [...(profile.languages || []), { language: newLang.language.trim(), level: newLang.level }]);
    setNewLang({ language: "", level: "B1" });
    toast.success(`${newLang.language.trim()} (${newLang.level})`);
  };

  const removeLanguage = (idx) => {
    const current = [...(profile.languages || [])];
    current.splice(idx, 1);
    updateField("languages", current);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await candidateAPI.updateProfile({
        nationality: profile.nationality,
        age: profile.age ? parseInt(profile.age) : null,
        location: profile.location,
        country: profile.country,
        phone: profile.phone,
        experience_years: profile.experience_years ? parseInt(profile.experience_years) : null,
        desired_positions: profile.desired_positions,
        languages: profile.languages,
        available_now: profile.available_now,
        available_from: profile.available_from,
        willing_to_relocate: profile.willing_to_relocate,
        expected_salary: profile.expected_salary ? parseFloat(profile.expected_salary) : null,
        bio: profile.bio,
        photo_url: profile.photo_url,
        needs_accommodation: profile.needs_accommodation,
        preferred_countries: profile.preferred_countries || [],
      });
      toast.success(t("cd.save"));
      setView("overview");
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error(t("cd.cv_pdf_only")); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(t("cd.cv_too_large")); return; }
    setUploadingCV(true);
    try {
      const res = await candidateAPI.uploadCV(file);
      setProfile(prev => ({ ...prev, cv_url: res.data.cv_url, cv_original_name: res.data.filename }));
      toast.success(t("cd.cv_uploaded"));
    } catch { toast.error("Error"); } finally { setUploadingCV(false); }
  };

  const handleCVDelete = async () => {
    try {
      await candidateAPI.deleteCV();
      setProfile(prev => ({ ...prev, cv_url: null, cv_original_name: null }));
      toast.success(t("cd.cv_deleted"));
    } catch { toast.error("Error"); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.toLowerCase().split(".").pop();
    if (!["jpg", "jpeg", "png"].includes(ext)) { toast.error(t("cd.photo_format_error")); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(t("cd.photo_format_error")); return; }
    setUploadingPhoto(true);
    try {
      const res = await candidateAPI.uploadPhoto(file);
      setProfile(prev => ({ ...prev, photo_url: res.data.photo_url }));
      toast.success(t("cd.photo_uploaded"));
    } catch { toast.error("Error"); } finally { setUploadingPhoto(false); }
  };

  const handlePhotoDelete = async () => {
    try {
      await candidateAPI.deletePhoto();
      setProfile(prev => ({ ...prev, photo_url: null }));
      toast.success(t("cd.photo_deleted"));
    } catch { toast.error("Error"); }
  };

  const handlePhotoClick = () => {
    if (!profile?.is_premium) { setShowPhotoUpsell(true); return; }
    setView("edit");
  };

  // ── Video handlers ──
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.toLowerCase().split(".").pop();
    if (!["mp4", "webm", "mov"].includes(ext)) { toast.error(t("cd.video_format")); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error(t("cd.video_too_large")); return; }
    setUploadingVideo(true);
    try {
      const res = await candidateAPI.uploadVideo(file);
      setProfile(prev => ({ ...prev, video_url: res.data.video_url, video_uploaded_at: new Date().toISOString() }));
      toast.success(t("cd.video_uploaded"));
    } catch { toast.error("Error"); } finally { setUploadingVideo(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }, audio: true });
      streamRef.current = stream;
      if (liveVideoRef.current) { liveVideoRef.current.srcObject = stream; liveVideoRef.current.play(); }
      const chunks = [];
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus"
        : MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "video/mp4";
      const mr = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 1500000 });
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        setRecordedBlob(blob);
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };
      mediaRecorderRef.current = mr;
      mr.start(100);
      setRecording(true);
      setRecordingTime(30);
      let sec = 30;
      timerRef.current = setInterval(() => {
        sec--;
        setRecordingTime(sec);
        if (sec <= 0) stopRecording();
      }, 1000);
    } catch { toast.error("Camera access denied"); }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const saveRecordedVideo = async () => {
    if (!recordedBlob) return;
    const ext = recordedBlob.type.includes("webm") ? "webm" : "mp4";
    const file = new File([recordedBlob], `presentation.${ext}`, { type: recordedBlob.type });
    if (file.size > 15 * 1024 * 1024) { toast.error(t("cd.video_too_large")); return; }
    setUploadingVideo(true);
    try {
      const res = await candidateAPI.uploadVideo(file);
      setProfile(prev => ({ ...prev, video_url: res.data.video_url, video_uploaded_at: new Date().toISOString() }));
      setRecordedBlob(null);
      toast.success(t("cd.video_uploaded"));
    } catch { toast.error("Error"); } finally { setUploadingVideo(false); }
  };

  const handleVideoDelete = async () => {
    try {
      await candidateAPI.deleteVideo();
      setProfile(prev => ({ ...prev, video_url: null, video_uploaded_at: null }));
      toast.success(t("cd.video_deleted"));
    } catch { toast.error("Error"); }
  };

  const discardRecording = () => {
    setRecordedBlob(null);
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };

  const [showComingSoon, setShowComingSoon] = useState(false);
  const handlePremium = () => setShowComingSoon(true);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="candidate-dashboard">
      {/* Welcome Popup */}
      <Dialog open={showWelcome} onOpenChange={setShowWelcome}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 rounded-lg shadow-2xl" data-testid="welcome-popup">
          <DialogTitle className="sr-only">{t("welcome.title")}</DialogTitle>
          <div className="bg-navy-900 px-6 pt-8 pb-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold-500/20 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-gold-400" />
            </div>
            <h2 className="font-sans text-xl font-bold text-white mb-1">{t("welcome.title")}</h2>
            <p className="text-sm text-gold-400 font-body">{t("welcome.good_luck")}</p>
          </div>
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-slate-600 font-body leading-relaxed">{t("welcome.message")}</p>
            <div className="bg-gold-50 border border-gold-200 rounded-md p-3 flex items-start gap-3">
              <Crown className="w-5 h-5 text-gold-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gold-800 font-body">{t("welcome.premium_hint")}</p>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <Button className="bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-lg w-full" onClick={() => { setShowWelcome(false); handlePremium(); }} data-testid="welcome-premium-btn">
                <Crown className="w-4 h-4 mr-2" /> {t("welcome.cta_premium")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="ghost" className="text-slate-500 hover:text-slate-700 font-body w-full" onClick={() => setShowWelcome(false)} data-testid="welcome-close-btn">
                {t("welcome.cta_close")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="bg-navy-900 border-b border-navy-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex items-center justify-between h-14">
          <Logo size="md" className="text-white" />
          <div className="flex items-center gap-3">
            <LanguageSelector variant="dark" />
            {profile?.is_premium && (
              <Badge className="bg-gold-500/10 text-gold-400 border-gold-500/20 font-body">
                <Crown className="w-3 h-3 mr-1" />{t("common.premium")}
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Views */}
      {view === "overview" ? (
        <OverviewView
          profile={profile}
          user={user}
          t={t}
          tc={tc}
          tp={tp}
          onEdit={() => setView("edit")}
          onPremium={handlePremium}
          onToggle={handleToggle}
          onLogout={logout}
          onDeleteOpen={() => setShowDeleteDialog(true)}
          onPhotoClick={handlePhotoClick}
          onMessages={() => setView("messages")}
          onSettings={() => setView("settings")}
          onJobs={() => setView("jobs")}
          unreadCount={unreadCount}
          setProfile={setProfile}
        />
      ) : view === "messages" ? (
        <div className="max-w-lg mx-auto px-4 md:px-6 py-6">
          <button onClick={() => { setView("overview"); loadUnread(); }} className="flex items-center gap-1 text-sm text-slate-500 hover:text-gold-600 font-body mb-4 transition-colors" data-testid="back-from-messages">
            <ChevronLeft className="w-4 h-4" /> {t("cd.back_overview")}
          </button>
          <MessagesView user={user} t={t} onPremium={handlePremium} />
        </div>
      ) : view === "settings" ? (
        <div className="max-w-lg mx-auto px-4 md:px-6 py-6">
          <SettingsView user={user} onBack={() => setView("overview")} onLogout={logout} onPremium={handlePremium} />
        </div>
      ) : view === "jobs" ? (
        <CandidateJobsView profile={profile} t={t} tc={tc} tp={tp} onBack={() => setView("overview")} />
      ) : (
        <EditProfileView
          profile={profile}
          user={user}
          t={t}
          tc={tc}
          tp={tp}
          onBack={() => setView("overview")}
          updateField={updateField}
          saveProfile={saveProfile}
          saving={saving}
          uploadingCV={uploadingCV}
          handleCVUpload={handleCVUpload}
          handleCVDelete={handleCVDelete}
          togglePosition={togglePosition}
          newLang={newLang}
          setNewLang={setNewLang}
          addLanguage={addLanguage}
          removeLanguage={removeLanguage}
          langInputRef={langInputRef}
          uploadingPhoto={uploadingPhoto}
          handlePhotoUpload={handlePhotoUpload}
          handlePhotoDelete={handlePhotoDelete}
          onPhotoUpsell={() => setShowPhotoUpsell(true)}
          uploadingVideo={uploadingVideo}
          handleVideoUpload={handleVideoUpload}
          handleVideoDelete={handleVideoDelete}
          recording={recording}
          recordedBlob={recordedBlob}
          recordingTime={recordingTime}
          videoPreviewRef={videoPreviewRef}
          liveVideoRef={liveVideoRef}
          startRecording={startRecording}
          stopRecording={stopRecording}
          saveRecordedVideo={saveRecordedVideo}
          discardRecording={discardRecording}
          onVideoUpsell={() => setShowVideoUpsell(true)}
        />
      )}

      {/* Photo Upsell Dialog */}
      <Dialog open={showPhotoUpsell} onOpenChange={setShowPhotoUpsell}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden border-0 rounded-lg shadow-2xl" data-testid="photo-upsell-dialog">
          <DialogTitle className="sr-only">{t("cd.photo_upsell_title")}</DialogTitle>
          <div className="bg-navy-900 px-6 pt-7 pb-5 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gold-500/20 flex items-center justify-center">
              <Camera className="w-7 h-7 text-gold-400" />
            </div>
            <h2 className="font-sans text-lg font-bold text-white mb-1">{t("cd.photo_upsell_title")}</h2>
            <p className="text-sm text-slate-300 font-body">{t("cd.photo_upsell_desc")}</p>
          </div>
          <div className="px-6 py-5 flex flex-col gap-2">
            <Button
              className="bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-lg w-full h-11"
              onClick={() => { setShowPhotoUpsell(false); handlePremium(); }}
              data-testid="photo-upsell-upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" /> {t("cd.upsell_btn")}
            </Button>
            <Button variant="ghost" className="text-slate-500 hover:text-slate-700 font-body w-full" onClick={() => setShowPhotoUpsell(false)} data-testid="photo-upsell-cancel-btn">
              {t("common.cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Coming Soon Dialog */}
      <Dialog open={showComingSoon} onOpenChange={setShowComingSoon}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden border-0 rounded-lg shadow-2xl" data-testid="coming-soon-dialog">
          <DialogTitle className="sr-only">{t("premium.coming_soon_title")}</DialogTitle>
          <div className="bg-navy-900 px-6 pt-7 pb-5 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gold-500/20 flex items-center justify-center">
              <Crown className="w-7 h-7 text-gold-400" />
            </div>
            <h2 className="font-sans text-lg font-bold text-white mb-1">{t("premium.coming_soon_title")}</h2>
            <p className="text-sm text-slate-300 font-body">{t("premium.coming_soon_desc")}</p>
          </div>
          <div className="px-6 py-5">
            <Button variant="outline" className="w-full" onClick={() => setShowComingSoon(false)} data-testid="coming-soon-close-btn">
              {t("common.close")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Upsell Dialog */}
      <Dialog open={showVideoUpsell} onOpenChange={setShowVideoUpsell}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden border-0 rounded-lg shadow-2xl" data-testid="video-upsell-dialog">
          <DialogTitle className="sr-only">{t("cd.video_upsell_title")}</DialogTitle>
          <div className="bg-navy-900 px-6 pt-7 pb-5 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gold-500/20 flex items-center justify-center">
              <Video className="w-7 h-7 text-gold-400" />
            </div>
            <h2 className="font-sans text-lg font-bold text-white mb-1">{t("cd.video_upsell_title")}</h2>
            <p className="text-sm text-slate-300 font-body">{t("cd.video_upsell_desc")}</p>
          </div>
          <div className="px-6 py-5 flex flex-col gap-2">
            <Button
              className="bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-lg w-full h-11"
              onClick={() => { setShowVideoUpsell(false); handlePremium(); }}
              data-testid="video-upsell-upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" /> {t("cd.upsell_btn")}
            </Button>
            <Button variant="ghost" className="text-slate-500 hover:text-slate-700 font-body w-full" onClick={() => setShowVideoUpsell(false)} data-testid="video-upsell-cancel-btn">
              {t("common.cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(v) => { setShowDeleteDialog(v); if (!v) setDeleteConfirm(""); }}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0" data-testid="delete-account-dialog">
          <DialogTitle className="sr-only">{t("account.delete_title")}</DialogTitle>
          <div className="bg-red-600 px-6 pt-6 pb-4 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-white" />
            </div>
            <h2 className="font-sans text-lg font-bold text-white">{t("account.delete_title")}</h2>
          </div>
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-slate-600 font-body leading-relaxed">{t("account.delete_desc")}</p>
            <div>
              <Label className="text-sm font-body text-slate-500 mb-1 block">{t("account.delete_confirm")}</Label>
              <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={t("account.delete_keyword")} className="h-10 font-body border-red-200 focus:border-red-400" data-testid="delete-confirm-input" />
            </div>
            <div className="flex gap-3 pt-1">
              <Button variant="outline" className="flex-1 font-body" onClick={() => { setShowDeleteDialog(false); setDeleteConfirm(""); }} data-testid="delete-cancel-btn">
                {t("common.cancel")}
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
                disabled={deleteConfirm !== t("account.delete_keyword") || deleting}
                onClick={async () => {
                  setDeleting(true);
                  try { await authAPI.deleteAccount(); logout(); navigate("/", { replace: true }); }
                  catch { toast.error("Error"); setDeleting(false); }
                }} data-testid="delete-account-confirm-btn">
                <Trash2 className="w-4 h-4 mr-2" /> {deleting ? "..." : t("account.delete_btn")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
